import { useState, useRef, useEffect } from 'react';
import { Globe } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

const languages = [
  { code: 'sv' as const, label: 'Svenska', flag: '\u{1F1F8}\u{1F1EA}' },
  { code: 'en' as const, label: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
];

export function LanguagePicker() {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === language) ?? languages[0];

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm hover:bg-gray-100 transition-colors"
        aria-label="Change language"
      >
        <Globe className="h-4 w-4" />
        <span>{current.flag}</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-1 w-40 rounded-lg border bg-white shadow-lg z-50">
          {languages.map((lng) => (
            <button
              key={lng.code}
              type="button"
              onClick={() => {
                setLanguage(lng.code);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                lng.code === language ? 'font-semibold bg-gray-50' : ''
              }`}
            >
              <span>{lng.flag}</span>
              <span>{lng.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
